import React, { useContext } from 'react';
import styles from "@/components/PLP-2.0/PLPSelectedFiltersComp/PLPSelectedFilters.module.scss";
import useMediaQuery from "@/helpers/hooks/useMediaQuery";
import { normalizeLabel, getPriceSelectedLabel} from "@/helpers/plpHelper";
import { generalConstants } from "@/utils/constants/general";
import { HiddenFiltersContext } from "./HiddenFiltersContext";
import { setCookie } from 'cookies-next';

// Helper to extract the correct label for display
function getDisplayLabel(value) {
  if (!value || typeof value !== "string") return value;
  const parts = value.split(">");
  if (parts.length === 1) {
    // Always show the lowest-case variant for single-part filters
    return value;
  }
  // For multi-part, always show the last part (e.g. "Small hats" for "Bags & Accessories > Hats > Small hats")
  return parts[parts.length - 1].trim();
}

// Order for displaying filter pills
const FILTER_ORDER = [
  generalConstants.FILTER_KEYS.CATEGORY,
  generalConstants.FILTER_KEYS.SUBCATEGORY,
  generalConstants.FILTER_KEYS.LEVEL2,
  generalConstants.FILTER_KEYS.COLOR,
  generalConstants.FILTER_KEYS.SIZE,
  generalConstants.FILTER_KEYS.PRICE,
  generalConstants.FILTER_KEYS.OCCASION,
  generalConstants.FILTER_KEYS.SLEEVES,
  generalConstants.FILTER_KEYS.DESIGNERS,
  generalConstants.FILTER_KEYS.AVAILABILITY,
];

const SelectedFiltersBar = ({
  selectedFilters,
  setFilters,
  maxDesktop = 7,
  maxTablet = 4,
}) => {
  // Responsive helpers
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1023px)");
  const { hiddenFilters } = useContext(HiddenFiltersContext);

  // Flatten all selected filters into an array of { facetKey, value }
  let filterItems = Object.entries(selectedFilters)
    .flatMap(([facetKey, values]) => {
      if (!Array.isArray(values)) return [];
      if (facetKey === generalConstants.FILTER_KEYS.PRICE) {
        return values
          .filter(
            v =>
              typeof v === "object" &&
              (v.gte !== undefined || v.lte !== undefined)
          )
          .map((value) => ({
            facetKey,
            value,
            displayLabel: getPriceSelectedLabel(value)
          }));
      }
      // For other filters, keep as before
      return values.map((value) => ({ facetKey, value }));
    });

  // Map to display label and remove duplicates (case/space-insensitive)
  const seen = new Set();
  filterItems = filterItems
    .map(item => ({
      ...item,
      displayLabel:
        item.displayLabel !== undefined
          ? item.displayLabel
          : getDisplayLabel(item.value)
    }))
    .filter(item => {
      const norm = normalizeLabel(item.displayLabel);
      if (seen.has(norm)) return false;
      seen.add(norm);
      return true;
    });

  // Sort by filter order
  filterItems = filterItems.sort((a, b) => {
    const aIdx = FILTER_ORDER.indexOf(a.facetKey);
    const bIdx = FILTER_ORDER.indexOf(b.facetKey);
    return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
  });

  const sumOfEle = filterItems.length;

  // Always show the count, even if pills are hidden
  return (
    <div className={styles.filter_view}>
      <div className={styles.filter_total}>
      {sumOfEle > 0 && (
        <>
          <span className="x-small-sm x-small-md xx-small-xs">{sumOfEle}</span>
          <div
            className={styles.filter_remove}
            onClick={() => handleRemoveFilter("ALL")}
            title="Clear all filters"
          >
            <img
              src={`${process.env.NETLIFY_URL}/icons/Cross/Large-sm.svg`}
              alt="filter remove"
            />
          </div>
        </>
      )}
    </div>
      {/* Only show filter pills if not exceeding max */}
      {((isDesktop && sumOfEle <= maxDesktop) || (isTablet && sumOfEle <= maxTablet)) &&
        filterItems.map((item, index) => (
          <div className={styles.filter_item} key={item.facetKey + item.value + index}>
            <div className={`${styles.select_filter} small`}>{item.displayLabel}</div>
            <div
              className={styles.filter_remove}
              onClick={() => handleRemoveFilter(item.facetKey, item.value)}
              title="Remove filter"
            >
              <img
                src={`${process.env.NETLIFY_URL}/icons/Cross/Large-sm.svg`}
                alt="filter remove"
              />
            </div>
          </div>
        ))}
    </div>
  );

  function handleRemoveFilter(facetKey, value) {
    if (facetKey === "ALL") {
      // Remove all filters except hidden filters from URL and reload
      const url = new URL(window.location.href);
      url.searchParams.delete("filters");
      url.searchParams.delete("sort");
      if (hiddenFilters && Object.keys(hiddenFilters).length > 0) {
        url.searchParams.set("filters", JSON.stringify(hiddenFilters));
      }
      window.location.replace(url.toString()); // Reload page with cleaned URL
      return;
    } else {
      setCookie('reapplyFilters',true)
      setFilters(prev => {
        const updated = { ...prev };
        if (Array.isArray(updated[facetKey])) {
          const normToRemove = normalizeLabel(value);
          updated[facetKey] = updated[facetKey].filter(
            v => normalizeLabel(v) !== normToRemove
          );
          // Remove the key if the array is empty
          if (updated[facetKey].length === 0) {
            delete updated[facetKey];
          }
        }
        return updated;
      });
    }
  }
};

export default SelectedFiltersBar;